import React, {createContext, useCallback, useContext, useEffect, useMemo} from 'react';
import UserFlowFacade, {
  UserFlowMapKeys,
  UserFlowType,
} from '../../../Facade/UserFlow/UserFlowFacade';
import {useSelector} from 'react-redux';
import useSettings from '../../../Hooks/useSettings';
import {LayoutSelector} from '../../../Selectors';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useUiState} from '../UserInterface/UserInterfaceContext';
import {OnLoadReason} from '@taopix/taopix-core';
import {AppActions} from '../AppActionTypes';
import {useTaopix} from '../TaopixContext';
import {UserFlowDirection, UserFlowList, UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {ROUTE_ASSISTANT} from '../../../Constants';
import {ExperienceType, ProductLayout} from '@taopix/taopix-core/dist/types/types';

export type UserFlowState = {
  instance: UserFlowFacade;
  stage: UserFlowMapKeys;
  children?: any;
};

export type ContextState = {
  initialise: (experince: any) => void;
  setStage: (stage: UserFlowType) => void;
  getStateStage: () => UserFlowType;
  activateUserFlow: (userFlow: UserFlowType) => void;
  deactivateUserFlow: (userFlow: UserFlowType) => void;
  getActiveStages: () => string[];
  setNextStep: () => void;
  getNextStep: (userFlowType: UserFlowType) => string;
  setPreviousStep: () => void;
  setCurrentStep: (step: string) => void;
  getCurrentStep: (userFlowType?: UserFlowType) => string;
  getSteps: (userFlowType: UserFlowType) => UserFlowList;
  resetStep: () => void;
  addSkipStep: (direction: UserFlowDirection, step: string) => void;
  removeSkipStep: (direction: UserFlowDirection, step: string) => void;
  setInactive: (userFlow: UserFlowType) => void;
  isUserFlowActive: (userFlow: UserFlowType) => boolean;
  isStepActive: (userFlowType: UserFlowType, userFlowStep: UserFlowStep) => boolean;
  setExperience: (experience: ExperienceType) => void;
  setProductLayout: (productLayout: ProductLayout) => void;
};

export const UserFlowContext = createContext<ContextState>({} as ContextState);

export const UserFlowProvider = props => {
  const {children} = props;
  const {state: settings} = useSettings();
  const productLayout = useSelector(LayoutSelector);
  const {experience} = useExperience();
  const uiState = useUiState();
  const app = useTaopix();

  const [state, setState] = React.useState({
    instance: UserFlowFacade.create(settings, productLayout, uiState.state, experience),
    stage: UserFlowType.UserFlow,
  });

  const onAssistantLoadCallback = useCallback(data => {
    UserFlowFacade.create(
      data.designerSettings,
      data.project.layout,
      uiState.state,
      data.experience
    );

    if (`#/${ROUTE_ASSISTANT}/` === data.designerSettings.project.stage) {
      setStage(UserFlowType.Assistant);
      activateUserFlow(UserFlowType.Assistant);

      if (
        1 === data.designerSettings.authentication.guestWorkflowMode &&
        !data.designerSettings.authentication.userRegistered
      ) {
        // Guest access is not allowed
        // Handled by ForceLogin component.
      } else {
        activateUserFlow(UserFlowType.Start);
      }
    }

    app.dispatch(AppActions.setIsLoaded(true));
  }, []);

  const onLoadCallback = useCallback(data => {
    UserFlowFacade.create(
      data.designerSettings,
      data.project.layout,
      uiState.state,
      data.experience
    );

    if (
      ['', '#/'].includes(window.location.hash) &&
      OnLoadReason.ProjectOpen === data.options.reason
    ) {
      activateUserFlow(UserFlowType.Start);
    }
  }, []);

  const forceRender = (changes: Partial<UserFlowState> = {}): void => {
    setState(current => {
      return {...current, ...changes};
    });
  };

  const initialise = (experience: any): void => {
    forceRender();
  };

  const setStage = (stage: UserFlowType): void => {
    state.instance.setUserFlowStage(stage);
    forceRender();
  };

  const getStateStage = (): UserFlowType => {
    return state.stage;
  };

  const activateUserFlow = (userFlow: UserFlowType): void => {
    state.instance.activateUserFlow(userFlow);
    forceRender({
      stage: userFlow,
    });
  };

  const deactivateUserFlow = (userFlow: UserFlowType): void => {
    state.instance.deactivateUserFlow(userFlow);
    forceRender();
  };

  const getActiveStages = (): string[] => {
    return state.instance.getActiveStages();
  };

  const setNextStep = (): void => {
    state.instance.setNextStep();
    forceRender();
  };

  const getNextStep = (userFlowType: UserFlowType): string => {
    return state.instance.getNextStep(userFlowType);
  };

  const setPreviousStep = (): void => {
    state.instance.setPreviousStep();
    forceRender();
  };

  const setCurrentStep = (step: string): void => {
    state.instance.setCurrentStep(step);
    forceRender();
  };

  const getCurrentStep = (userFlowType?: UserFlowType): string => {
    return state.instance.getCurrentStep(userFlowType);
  };

  const getSteps = (userFlowType: UserFlowType): UserFlowList => {
    return state.instance.getSteps(userFlowType);
  };

  const resetStep = () => {
    state.instance.resetStep();
  };

  const addSkipStep = (direction: UserFlowDirection, step): void => {
    state.instance.addSkipStep(direction, step);
    forceRender();
  };

  const removeSkipStep = (direction: UserFlowDirection, step): void => {
    state.instance.removeSkipStep(direction, step);
    forceRender();
  };

  const setInactive = (userFlow: UserFlowType): void => {
    state.instance.setInactive(userFlow);
    forceRender();
  };

  const isUserFlowActive = (userFlow: UserFlowType): boolean => {
    return state.instance.isUserFlowActive(userFlow);
  };

  const isStepActive = (userFlowType: UserFlowType, userFlowStep: UserFlowStep): boolean => {
    return state.instance.isStepActive(userFlowType, userFlowStep);
  };

  const setExperience = (experience: ExperienceType): void => {
    UserFlowFacade.setExperience(experience);
  };

  const setProductLayout = (productLayout: ProductLayout): void => {
    UserFlowFacade.setProductLayout(productLayout);
  };

  const currentStep = state.instance.getCurrentStep();

  useEffect(() => {
    if (!app.state.loaded) return;
    if (Object.keys(uiState.state).length === 0) return;
    UserFlowFacade.setUI(uiState.state);
  }, [uiState.state]);

  useEffect(() => {
    if (!app.state.loaded) return;
    if (Object.keys(productLayout).length === 0) return;
    UserFlowFacade.setProductLayout(productLayout);
  }, [productLayout]);

  useEffect(() => {
    if (!app.state.loaded) return;
    if (Object.keys(settings).length === 0) return;
    UserFlowFacade.setDesignerSettings(settings);
  }, [settings]);

  const loader = app.core.get('loader');

  useEffect(() => {
    loader.on('onAssistantLoad', onAssistantLoadCallback).on('onLoad', onLoadCallback);

    return () => {
      loader.off('onAssistantLoad', onAssistantLoadCallback).off('onLoad', onLoadCallback);
    };
  }, [loader]);

  useEffect(() => {
    if (UserFlowStep.None === currentStep.split('.')[1]) {
      const activeStages = state.instance.getActiveStages();
      setStage((activeStages[activeStages.length - 2] as UserFlowType) || UserFlowType.UserFlow);
    }
  }, [currentStep]);

  const contextValue = useMemo(
    () => ({
      initialise,
      setStage,
      getStateStage,
      activateUserFlow,
      deactivateUserFlow,
      getActiveStages,
      setNextStep,
      getNextStep,
      setPreviousStep,
      setCurrentStep,
      getCurrentStep,
      getSteps,
      resetStep,
      addSkipStep,
      removeSkipStep,
      setInactive,
      isStepActive,
      isUserFlowActive,
      setExperience,
      setProductLayout,
    }),
    [state]
  );

  return <UserFlowContext.Provider value={contextValue}>{children}</UserFlowContext.Provider>;
};

export const useUserFlowState = () => {
  const context = useContext(UserFlowContext);
  if (undefined === context) {
    throw new Error('useUserFlowState must be used within an UserFlowProvider');
  }
  return context;
};
